// Insurance Calculations - Pure functions for Swiss social insurance deductions
// No API calls, no side effects. All rates are passed in as parameters.

// Default PK age tables (Stiftung Abendrot) — separate M/F, single-age lookup [{alter, satz}]
export const PK_DEFAULT_ALTERSTABELLE_M = [
  { alter: 25, satz: 9 }, { alter: 35, satz: 12 }, { alter: 45, satz: 16 }, { alter: 55, satz: 19 },
];
export const PK_DEFAULT_ALTERSTABELLE_F = [
  { alter: 25, satz: 9 }, { alter: 35, satz: 12 }, { alter: 45, satz: 16 }, { alter: 55, satz: 19 },
];

// Calculate age at a given date from birthdate string (ISO)
export const getAgeAtDate = (geburtsdatum, date) => {
  if (!geburtsdatum) return null;
  const birth = new Date(geburtsdatum);
  const d = date instanceof Date ? date : new Date(date);
  let age = d.getFullYear() - birth.getFullYear();
  const monthDiff = d.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && d.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const round2 = (n) => Math.round(n * 100) / 100;

// AHV/IV/EO/ALV
export const calcAHV = (effektiverBrutto, rates) => {
  if (!rates) return { anAbzug: 0, agKosten: 0 };
  return {
    anAbzug: round2(effektiverBrutto * (rates.satzAN || 0) / 100),
    agKosten: round2(effektiverBrutto * (rates.satzAG || 0) / 100),
  };
};

// FAK (only AG, no AN)
export const calcFAK = (effektiverBrutto, rates) => {
  if (!rates) return { anAbzug: 0, agKosten: 0 };
  return {
    anAbzug: 0,
    agKosten: round2(effektiverBrutto * (rates.satzAG || 0) / 100),
  };
};

// SUVA (gender-dependent, with UVG salary cap)
export const calcSUVA = (effektiverBrutto, geschlecht, rates) => {
  if (!rates || !geschlecht) return { anAbzug: 0, agKosten: 0 };
  const g = geschlecht.toUpperCase();
  const nbuAN = rates[`nbuAN_${g}`] || 0;
  const nbuAG = rates[`nbuAG_${g}`] || 0;
  const buAG = rates[`buAG_${g}`] || 0;
  // Apply UVG salary cap (maxVersicherterLohn is yearly, effektiverBrutto is monthly)
  let basis = effektiverBrutto;
  if (rates.maxVersicherterLohn && rates.maxVersicherterLohn > 0) {
    basis = Math.min(effektiverBrutto, rates.maxVersicherterLohn / 12);
  }
  return {
    anAbzug: round2(basis * nbuAN / 100),
    agKosten: round2(basis * (nbuAG + buAG) / 100),
  };
};

// Krankentaggeld (gender-dependent, with UVG salary cap)
export const calcKTG = (effektiverBrutto, geschlecht, rates) => {
  if (!rates || !geschlecht) return { anAbzug: 0, agKosten: 0 };
  const g = geschlecht.toUpperCase();
  // Apply UVG salary cap (maxVersicherterLohn is yearly, effektiverBrutto is monthly)
  let basis = effektiverBrutto;
  if (rates.maxVersicherterLohn && rates.maxVersicherterLohn > 0) {
    basis = Math.min(effektiverBrutto, rates.maxVersicherterLohn / 12);
  }
  return {
    anAbzug: round2(basis * (rates[`satzAN_${g}`] || 0) / 100),
    agKosten: round2(basis * (rates[`satzAG_${g}`] || 0) / 100),
  };
};

// Pensionskasse (gender + age dependent, with Koordinationsabzug, Eintrittsschwelle, salary cap, anteilAG)
// New model: separate M/F alterstabelle [{alter, satz}], single-age lookup (highest alter <= personAge)
// gesamtBeitrag = versicherterLohnMonat * satz%, then split by anteilAG%
export const calcPK = (effektiverBrutto, geschlecht, alter, rates) => {
  if (!rates || !geschlecht || alter == null) return { anAbzug: 0, agKosten: 0 };

  // Cap salary at maxVersicherterLohn (yearly) before any deductions
  let jahreslohn = effektiverBrutto * 12;
  if (rates.maxVersicherterLohn && rates.maxVersicherterLohn > 0) {
    jahreslohn = Math.min(jahreslohn, rates.maxVersicherterLohn);
  }
  if (jahreslohn < (rates.eintrittsschwelle || 0)) return { anAbzug: 0, agKosten: 0 };

  const versicherterLohnJahr = Math.max(0, jahreslohn - (rates.koordinationsabzug || 0));
  const versicherterLohnMonat = versicherterLohnJahr / 12;

  // Select gender-specific table
  const g = geschlecht.toUpperCase();
  const tabelle = g === "M"
    ? (rates.alterstabelleMaenner || rates.alterstabelle || [])
    : (rates.alterstabelleFrauen || rates.alterstabelle || []);

  // Single-age lookup: find highest alter <= person's age
  let satz = 0;
  if (tabelle.length > 0) {
    if (tabelle[0].alterVon !== undefined) {
      // Legacy range format: { alterVon, alterBis, satzAN_M, satzAG_M, ... }
      const stufe = tabelle.find((s) => alter >= s.alterVon && alter <= s.alterBis);
      if (!stufe) return { anAbzug: 0, agKosten: 0 };
      return {
        anAbzug: round2(versicherterLohnMonat * (stufe[`satzAN_${g}`] || 0) / 100),
        agKosten: round2(versicherterLohnMonat * (stufe[`satzAG_${g}`] || 0) / 100),
      };
    }
    // New format: [{alter, satz}] — find highest alter <= person's age
    const sorted = [...tabelle].sort((a, b) => a.alter - b.alter);
    const match = sorted.filter((s) => s.alter <= alter);
    if (match.length > 0) {
      satz = match[match.length - 1].satz;
    }
  }
  if (!satz) return { anAbzug: 0, agKosten: 0 };

  const gesamtBeitrag = round2(versicherterLohnMonat * satz / 100);
  const anteilAG = rates.anteilAG || 50;
  return {
    anAbzug: round2(gesamtBeitrag * (100 - anteilAG) / 100),
    agKosten: round2(gesamtBeitrag * anteilAG / 100),
  };
};

// FAK Zulagen (Kinderzulagen / Ausbildungszulagen)
// kinder: [{ vorname, geburtsdatum, inAusbildung }]
// rates: { kinderzulage, ausbildungszulage, ... }
export const calcFAKZulagen = (kinder, rates, stichtag) => {
  if (!kinder || !kinder.length || !rates) {
    return { kinderzulagen: 0, ausbildungszulagen: 0, totalZulagen: 0 };
  }
  const d = stichtag instanceof Date ? stichtag : new Date(stichtag);
  let kinderzulagen = 0;
  let ausbildungszulagen = 0;

  for (const kind of kinder) {
    const alter = getAgeAtDate(kind.geburtsdatum, d);
    if (alter == null || alter < 0) continue;
    if (alter < 16) {
      kinderzulagen += rates.kinderzulage || 0;
    } else if (alter <= 25 && kind.inAusbildung) {
      ausbildungszulagen += rates.ausbildungszulage || 0;
    }
  }

  return {
    kinderzulagen: round2(kinderzulagen),
    ausbildungszulagen: round2(ausbildungszulagen),
    totalZulagen: round2(kinderzulagen + ausbildungszulagen),
  };
};

// FAK Netto = AG-Beitrag minus Zulagen (can be negative → Einnahme)
export const calcFAKNetto = (effektiverBrutto, kinder, rates, stichtag) => {
  if (!rates) return { beitragAG: 0, kinderzulagen: 0, ausbildungszulagen: 0, totalZulagen: 0, netto: 0 };

  const beitragAG = round2(effektiverBrutto * (rates.satzAG || 0) / 100);

  // Only grant zulagen if income meets minimum threshold
  if (!effektiverBrutto || effektiverBrutto < (rates.minEinkommenMonat || 0)) {
    return { beitragAG, kinderzulagen: 0, ausbildungszulagen: 0, totalZulagen: 0, netto: beitragAG };
  }

  const zulagen = calcFAKZulagen(kinder, rates, stichtag);
  return {
    beitragAG,
    ...zulagen,
    netto: round2(beitragAG - zulagen.totalZulagen),
  };
};

// Manual PK contributions: fixed monthly CHF amounts entered per person by GL,
// used instead of calcPK when the active PK rate has berechnungsModus === "manuell".
export const calcPKManuell = (pkManuell) => {
  if (!pkManuell) return { anAbzug: 0, agKosten: 0 };
  return {
    anAbzug: round2(pkManuell.anBeitrag || 0),
    agKosten: round2(pkManuell.agBeitrag || 0),
  };
};

// Update reasons for a person's manual PK entry. Returns [] when up to date.
// context: { jahr: current year, pensum: effective pensum (%), salaryId: id of the
// active salary entry ("" if none) }
export const getPkManualUpdateReasons = (pkEntry, context) => {
  if (!pkEntry) return ["Neueintritt / noch nicht erfasst"];
  const reasons = [];
  if ((pkEntry.standJahr || 0) !== context.jahr) reasons.push("Jahreswechsel");
  if (
    pkEntry.standPensum != null &&
    context.pensum != null &&
    parseFloat(pkEntry.standPensum) !== parseFloat(context.pensum)
  ) {
    reasons.push("Pensumänderung");
  }
  if ((pkEntry.standSalaryId || "") !== (context.salaryId || "")) reasons.push("Lohnänderung");
  return reasons;
};

// Calculate all deductions for a person in a given month
// effektiverBrutto = brutto * (pensum / 100) — already adjusted by caller
// options.pkManuell: { anBeitrag, agBeitrag } — used when allRates.pk.berechnungsModus === "manuell"
export const calcAllDeductions = (effektiverBrutto, geschlecht, alter, allRates, options = {}) => {
  if (!effektiverBrutto || effektiverBrutto <= 0) {
    return { deductions: [], totalAN: 0, totalAG: 0, netto: 0 };
  }

  const deductions = [];

  const ahv = calcAHV(effektiverBrutto, allRates?.ahv);
  if (ahv.anAbzug || ahv.agKosten) {
    deductions.push({ type: "ahv", label: "AHV/IV/EO/ALV", ...ahv });
  }

  const fak = calcFAK(effektiverBrutto, allRates?.fak);
  if (fak.agKosten) {
    deductions.push({ type: "fak", label: "FAK", ...fak });
  }

  const suva = calcSUVA(effektiverBrutto, geschlecht, allRates?.suva);
  if (suva.anAbzug || suva.agKosten) {
    deductions.push({ type: "suva", label: "SUVA NBU", ...suva });
  }

  const ktg = calcKTG(effektiverBrutto, geschlecht, allRates?.ktg);
  if (ktg.anAbzug || ktg.agKosten) {
    deductions.push({ type: "ktg", label: "Krankentaggeld", ...ktg });
  }

  const pk = allRates?.pk?.berechnungsModus === "manuell"
    ? calcPKManuell(options.pkManuell)
    : calcPK(effektiverBrutto, geschlecht, alter, allRates?.pk);
  if (pk.anAbzug || pk.agKosten) {
    deductions.push({ type: "pk", label: "Pensionskasse", ...pk });
  }

  const totalAN = round2(deductions.reduce((sum, d) => sum + d.anAbzug, 0));
  const totalAG = round2(deductions.reduce((sum, d) => sum + d.agKosten, 0));
  const netto = round2(effektiverBrutto - totalAN);

  return { deductions, totalAN, totalAG, netto };
};
